import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, FileText, AlertCircle, Phone, Car, Lock, KeyRound, CreditCard, Truck, Camera, ScanSearch, Star } from "lucide-react";

export function HowItWorks() {
  return (
    <div className="relative">
      <Hero
        title="From search to keys, in minutes."
        body="Whether you’re heading to a wedding in Knysna or a meeting in Sandton, tadada gets you behind the wheel safely and quickly."
      />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-3 gap-6 -mt-12">
        {[
          { icon: <ScanSearch size={22} />, t: "Search & compare", b: "Filter by city, brand, transmission, fuel and budget. Map and list views available." },
          { icon: <CreditCard size={22} />, t: "Book & pay securely", b: "Card or EFT. Refundable deposit on hold. Instant confirmation." },
          { icon: <KeyRound size={22} />, t: "Collect or get delivery", b: "Meet your host or schedule door delivery. Snap before-photos." },
          { icon: <Car size={22} />, t: "Drive", b: "Self-drive or with a chauffeur. Roadside assistance one tap away." },
          { icon: <Camera size={22} />, t: "Return", b: "Drop off at agreed time. Snap after-photos. We release your deposit." },
          { icon: <Star size={22} />, t: "Rate & review", b: "Help the next driver. Build the most trusted marketplace in SA." },
        ].map((s, i) => (
          <div key={i} className="rounded-2xl border border-[#E2E8F0] p-6 bg-white">
            <div className="w-11 h-11 rounded-xl bg-[#08153a] text-white flex items-center justify-center mb-4">{s.icon}</div>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Step {i + 1}</p>
            <h3 className="font-display text-lg font-bold text-[#08153a] mt-1">{s.t}</h3>
            <p className="text-sm text-[#334155] mt-2 leading-relaxed">{s.b}</p>
          </div>
        ))}
      </div>
      <div className="text-center mt-20">
        <Link to="/vehicles" className="btn-primary text-sm">Browse cars now</Link>
      </div>
    </div>
  );
}

export function Trust() {
  return (
    <div className="relative">
      <Hero kicker="Trust & safety" title="We obsess about safety so you don't have to." body="Every host, vehicle and booking on Avian goes through a strict trust workflow." />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-5 -mt-12">
        {[
          { icon: <ShieldCheck size={20} />, t: "Insurance on every booking", b: "Comprehensive cover with reduced excess via our partner network. Add Premium Insurance for the lowest excess." },
          { icon: <BadgeCheck size={20} />, t: "Certified inspections", b: "Every vehicle is inspected by a certified workshop before going live and re-checked every 90 days." },
          { icon: <FileText size={20} />, t: "Verified ownership", b: "We verify each owner’s ID, licence disc, ownership proof and roadworthy certificate." },
          { icon: <Lock size={20} />, t: "Refundable deposit", b: "A small refundable deposit protects against damage and late returns. Released within 48 hours." },
          { icon: <Camera size={20} />, t: "Before / after photos", b: "Required at pickup and drop-off. Settled disputes in days, not weeks." },
          { icon: <Phone size={20} />, t: "24/7 roadside assistance", b: "Tap once in your dashboard to reach tadada's emergency line." },
          { icon: <AlertCircle size={20} />, t: "Damage reporting", b: "A simple workflow with photos, notes and an assigned case manager." },
          { icon: <Truck size={20} />, t: "Safe delivery", b: "Drivers vetted, licensed and rated. GPS tracked from depot to door." },
        ].map((c, i) => (
          <div key={i} className="rounded-2xl border border-[#E2E8F0] p-6 bg-white">
            <div className="w-10 h-10 rounded-xl bg-[#1d7ffd]/10 text-[#1d7ffd] flex items-center justify-center mb-3">{c.icon}</div>
            <h3 className="font-display text-lg font-bold text-[#08153a]">{c.t}</h3>
            <p className="text-sm text-[#334155] mt-2 leading-relaxed">{c.b}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Host() {
  return (
    <div className="relative">
      <Hero kicker="Earn with tadada" title="List your car. Make up to R12,500 / month." body="Hosts on tadada earn passive income with the safety net of insurance, inspection and 24/7 support." />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 -mt-8">
        <div className="lg:col-span-7 rounded-[2rem] overflow-hidden aspect-[4/3]">
          <img src="https://images.pexels.com/photos/19143577/pexels-photo-19143577.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=720&w=960" alt="Host your car" className="w-full h-full object-cover" />
        </div>
        <div className="lg:col-span-5">
          {[
            { t: "Set your price & calendar", b: "Hourly, daily, weekly or monthly. Your car, your rules." },
            { t: "Get verified renters", b: "We check ID and licence so you don't have to." },
            { t: "Insurance built in", b: "Comprehensive cover during every booking." },
            { t: "Payouts every Friday", b: "Direct to your bank account in ZAR." },
          ].map((s, i) => (
            <div key={i} className="border-b border-[#E2E8F0] py-4">
              <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">Step {i + 1}</p>
              <h3 className="font-display text-lg font-bold text-[#08153a] mt-1">{s.t}</h3>
              <p className="text-sm text-[#334155] mt-1">{s.b}</p>
            </div>
          ))}
          <Link to="/register" className="btn-primary text-sm mt-6 inline-flex" data-testid="host-register-cta">Start listing</Link>
        </div>
      </div>
    </div>
  );
}

const Hero = ({ kicker, title, body }) => (
  <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-12 pb-20">
    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B]">{kicker}</p>
    <h1 className="font-brand text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0a1f4d] mt-3 max-w-3xl">{title}</h1>
    <p className="mt-4 text-lg text-[#334155] max-w-2xl leading-relaxed">{body}</p>
  </section>
);

const PRIVACY_SECTIONS = [
  { t: "Who we are", b: "tadada (Pty) Ltd operates the tadada website, progressive web app and mobile apps — a marketplace where car owners (\"hosts\") rent vehicles to customers (\"guests\"), optionally with a vetted chauffeur. We are the responsible party under the Protection of Personal Information Act, 2013 (POPIA). Contact: hello@tadada.co.za." },
  { t: "What we collect", b: "Account details (name, email, phone, city); identity and safety documents you upload for verification (ID, driver's licence, police clearance certificate for chauffeurs); booking details, handover photos and odometer readings; host bank details for payouts; device push-notification tokens; approximate location if you allow it to find cars near you. Card and EFT details are captured by PayFast and never reach tadada's servers." },
  { t: "Why we use it", b: "To create and secure your account, verify identities so the community stays safe, process bookings, deposits, refunds, late-return and excess-kilometre charges, pay hosts and chauffeurs, send booking confirmations, reminders and receipts (email, SMS, WhatsApp and push), resolve damage claims, and meet our legal and tax obligations." },
  { t: "Who we share it with", b: "The other party to your booking sees only what they need (name, phone, handover photos). Service providers who process data on our behalf: PayFast (payments), Twilio (SMS/WhatsApp), Expo (push notifications) and our hosting provider. Verification documents are reviewed by tadada staff only and are never shared with hosts or guests. We share data with authorities only when the law requires it." },
  { t: "How long we keep it", b: "Account and booking records are kept for five years after your last booking to satisfy tax and dispute obligations; verification documents are deleted 12 months after your account closes; handover photos 24 months after the trip." },
  { t: "Your rights", b: "You may access, correct or request deletion of your personal information, object to processing or lodge a complaint with the Information Regulator (inforeg.org.za). Email hello@tadada.co.za — we respond within 30 days. You can also withdraw push, SMS or WhatsApp consent at any time from Account settings." },
  { t: "Security", b: "Data is encrypted in transit (TLS) and at rest, access is role-based and logged, and documents are stored in private storage that requires authentication." },
  { t: "Children", b: "tadada is for people aged 18 and over. We do not knowingly collect information from children." },
  { t: "Changes", b: "We will notify you by email or in-app notice before material changes take effect. Last updated: February 2026." },
];

export function Privacy() {
  return (
    <div className="relative" data-testid="privacy-page">
      <Hero kicker="Privacy policy" title="Your data, handled with care." body="How tadada collects, uses and protects personal information — written for humans, compliant with POPIA." />
      <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 -mt-12 space-y-4 pb-20">
        {PRIVACY_SECTIONS.map((c) => (
          <section key={c.t} className="rounded-2xl border border-[#E2E8F0] p-6 bg-white">
            <h3 className="font-display text-lg font-bold text-[#08153a]">{c.t}</h3>
            <p className="text-sm text-[#334155] mt-2 leading-relaxed">{c.b}</p>
          </section>
        ))}
      </div>
    </div>
  );
}
